/**
 * AI Assistant avatar with sparkle SVG.
 * Static by default (midnight color); pass `animated` to enable the thinking animation.
 *
 * When animation stops, sparkles complete their current cycle and fade out
 * gracefully over 400ms instead of snapping to rest.
 *
 * Previous implementation (commented out for rollback):
 * ```tsx
 * <Box style={{
 *     width: '32px', height: '32px', borderRadius: '50%',
 *     backgroundColor: 'var(--swan-sem-color-bg-hover)',
 *     display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
 * }}>
 *     <Icon iconType="aiSupport" />
 * </Box>
 * ```
 */

import { useEffect, useRef, useState } from 'react';

interface AiAvatarProps {
    /** When true, sparkles animate (use during loading/thinking state) */
    animated?: boolean;
}

export function AiAvatar({ animated = false }: AiAvatarProps) {
    const fill = 'var(--swan-sem-color-brand-midnight, #2F3A53)';

    // Track whether we're in the wind-down phase (animated just turned off)
    const [windingDown, setWindingDown] = useState(false);
    const prevAnimated = useRef(animated);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (prevAnimated.current && !animated) {
            // Animation just stopped — enter wind-down phase
            setWindingDown(true);
            timerRef.current = setTimeout(() => setWindingDown(false), 500);
        }
        prevAnimated.current = animated;
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [animated]);

    const showAnimation = animated || windingDown;

    return (
        <svg
            width="32"
            height="32"
            viewBox="0 0 18 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ flexShrink: 0 }}
            aria-hidden="true"
        >
            <g
                className={
                    showAnimation
                        ? `ai-avatar-sparkle-1${windingDown ? ' ai-avatar-winding-down' : ''}`
                        : undefined
                }
            >
                <path
                    d="M5.52387 8.55391C5.39797 8.90842 5.24865 9.25252 5.0829 9.585C4.91715 9.91748 4.72798 10.2396 4.52239 10.55C4.31679 10.8605 4.08777 11.1606 3.84811 11.4408C3.60844 11.721 3.34535 11.9908 3.06462 12.242C2.78389 12.4931 2.49373 12.7315 2.18471 12.9441C1.8769 13.1638 1.55023 13.3579 1.20714 13.5403C1.58463 13.5978 1.95755 13.6706 2.32834 13.7726C2.69214 13.8759 3.05138 13.9944 3.39328 14.1376C3.74218 14.2796 4.07374 14.4464 4.40074 14.6283C4.72774 14.8103 5.03741 15.017 5.33552 15.2401C5.63363 15.4632 5.92018 15.7028 6.18817 15.9601C6.45617 16.2174 6.71261 16.4911 6.9505 16.7826C7.18839 17.074 7.40772 17.3831 7.60728 17.7029C7.80685 18.0227 7.98786 18.3601 8.15609 18.707L8.51629 19.4546L8.52724 19.3514L8.60383 18.6294C8.63847 18.2473 8.69532 17.8686 8.78259 17.4991C8.86286 17.1308 8.96534 16.7659 9.09124 16.4114C9.21714 16.0569 9.36646 15.7128 9.5322 15.3803C9.69795 15.0479 9.88712 14.7258 10.0927 14.4153C10.2983 14.1049 10.5273 13.8048 10.767 13.5246C11.0137 13.2432 11.2698 12.9746 11.5505 12.7234C11.8312 12.4722 12.1214 12.2339 12.4304 12.0212C12.7382 11.8015 13.0649 11.6075 13.408 11.425C13.0305 11.3675 12.6576 11.2948 12.2868 11.1927C11.923 11.0895 11.5637 10.9709 11.2218 10.8277C10.8799 10.6845 10.5414 10.519 10.2144 10.337C9.88736 10.1551 9.57769 9.94839 9.27959 9.72526C8.98148 9.50212 8.69493 9.26252 8.42693 9.00523C8.15893 8.74795 7.90249 8.4742 7.66461 8.18277C7.42672 7.89133 7.20738 7.58222 7.00782 7.26245C6.80826 6.94267 6.62725 6.60522 6.45901 6.25832L6.09881 5.51077L6.08787 5.61392L6.01127 6.33594C5.97663 6.71802 5.91978 7.09671 5.83251 7.46621C5.75224 7.8345 5.64976 8.19941 5.52387 8.55391Z"
                    fill={fill}
                />
            </g>
            <g
                className={
                    showAnimation
                        ? `ai-avatar-sparkle-2${windingDown ? ' ai-avatar-winding-down' : ''}`
                        : undefined
                }
            >
                <path
                    d="M12.7549 2.20472C12.6872 2.39531 12.6069 2.58031 12.5178 2.75906C12.4287 2.93782 12.327 3.11098 12.2164 3.27789C12.1059 3.44481 11.9828 3.60613 11.8539 3.75678C11.7251 3.90743 11.5836 4.05249 11.4327 4.18753C11.2818 4.32256 11.1258 4.4507 10.9596 4.56505C10.7941 4.68317 10.6185 4.78749 10.4341 4.88557C10.637 4.91649 10.8375 4.9556 11.0368 5.01047C11.2324 5.06599 11.4256 5.12971 11.6094 5.20671C11.797 5.28306 11.9752 5.37269 12.151 5.47052C12.3268 5.56835 12.4933 5.67946 12.6536 5.79942C12.8139 5.91939 12.9679 6.04821 13.112 6.18653C13.2561 6.32485 13.394 6.47203 13.5219 6.62872C13.6498 6.7854 13.7677 6.95159 13.875 7.12351C13.9823 7.29543 14.0796 7.47686 14.17 7.66336L14.3637 8.06526L14.3696 8.00981L14.4108 7.62163C14.4294 7.41621 14.4599 7.21261 14.5069 7.01396C14.55 6.81595 14.6051 6.61977 14.6728 6.42917C14.7405 6.23858 14.8208 6.05358 14.9099 5.87483C14.999 5.69607 15.1007 5.52291 15.2112 5.356C15.3218 5.18908 15.4449 5.02776 15.5737 4.87711C15.7064 4.72581 15.844 4.5814 15.995 4.44637C16.1459 4.31133 16.3019 4.18319 16.468 4.06884C16.6335 3.95072 16.8092 3.8464 16.9936 3.74832C16.7907 3.71741 16.5902 3.67829 16.3908 3.62342C16.1952 3.56791 16.0021 3.50419 15.8183 3.42718C15.6345 3.35018 15.4524 3.26121 15.2766 3.16338C15.1008 3.06555 14.9343 2.95443 14.7741 2.83447C14.6138 2.7145 14.4597 2.58569 14.3156 2.44736C14.1716 2.30904 14.0337 2.16186 13.9058 2.00518C13.7779 1.84849 13.66 1.6823 13.5527 1.51038C13.4454 1.33846 13.3481 1.15704 13.2576 0.970531L13.064 0.568627L13.0581 0.624082L13.0169 1.01226C12.9983 1.21768 12.9677 1.42128 12.9208 1.61993C12.8776 1.81794 12.8226 2.01412 12.7549 2.20472Z"
                    fill={fill}
                />
            </g>
        </svg>
    );
}
